import { useCallback, useMemo, useState, useEffect, useRef } from 'react';

import LayoutMain from '@Layouts/LayoutMain';
import * as ApiPromo from '@Api/Promo';
import { useDeps } from '@Contexts/DI';
import Button from '@UI/Button';
import UILink from '@UI/Link';
import ProductCard from '@Components/ProductCard';
import CardInView from '@Components/CardInView';
import useMedias from '@Hooks/useMedias';
import usePage from '@Queries/usePage';
import { useMeta } from '@Queries/Meta';
import useTranslation from '@Queries/useTranslation';
import PageTopBar from '@Components/PageTopBar';
import StepPeoples from './elements/StepPeoples';
import StepsIcons from './elements/StepsIcons';
import Page from '@Components/Page';
import formatPrice from '@Utils/formatPrice';
import StepWeight from './elements/StepWeight';
import NotFound from './elements/NotFound';
import StepHardness from './elements/StepHardness';
import StepType from './elements/StepType';
import StepPrice from './elements/StepPrice';
import Section from './elements/Section';
import ProductCatalog from './elements/ProductCatalog';
import Properties from './elements/Properties';
import Repeat from './elements/Repeat';
import Spinner from './elements/Spinner';
import styles from './PageQuizMattrasses.module.css';

import type { FC } from 'react';
import type { MattressesQuizLists, Params, Range } from '@Api/Promo/get-mattresses-products';
import type { PageQuizMattrassesData, StepData, VariantData } from './typings';

const PageQuizMattrasses: FC = () => {
  const { data: page } = usePage<PageQuizMattrassesData>();
  const { isMobileM } = useMedias();
  const { logger, analytics } = useDeps();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [lists, setLists] = useState<MattressesQuizLists>(null);
  const [steps, setSteps] = useState(page.steps);
  const [pages, setPages] = useState(page.pages);
  const [selectPrice, setSelectPrice] = useState<Range>(null);
  const [selectedProperties, setSelectedProperties] = useState(null);
  const [selectedSizes, setSelectedSizes] = useState(null);
  const selectedStep = steps.find((step) => step.selected) || steps[0];
  const selectedStepIndex = steps.findIndex((step) => step.id === selectedStep.id);
  const refSizes = useRef<HTMLDivElement>(null);
  const refInitialParams = useRef<Record<string, any>>({});
  const refOtherParams = useRef<Record<string, any>>({});
  const meta = useMeta();
  const { region } = meta?.data || {};
  const initialRegionRef = useRef(region?.id);

  const selectStep = useCallback((newStep) => {
    setSteps((prevSteps) => {
      return prevSteps.map((step) => ({
        ...step,
        selected: step.id === newStep.id,
      }));
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const selectPage = useCallback((newPage) => {
    setPages((prevPages) => {
      return prevPages.map((pageItem) => ({
        ...pageItem,
        selected: pageItem.id === newPage.id,
      }));
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const selectedVariants: Record<string, VariantData[]> = useMemo(() => {
    const variants = {};

    pages.forEach((pageItem) => {
      pageItem.variants.forEach((variant) => {
        if (variant.selected) {
          if (!variants[pageItem.step]) variants[pageItem.step] = [];

          variants[pageItem.step].push(variant);
        }
      });
    });

    return variants;
  }, [pages]);

  const selectedParams = useMemo(() => {
    const variantId = selectedVariants?.peoples ? selectedVariants.peoples[0].id : 'adults';
    const params = page.parameters.find((parameter) => parameter.id === variantId);
    const height = [...params.height];
    const width = [...params.width];

    //если пользователь выбрал селектом размер, ищем в выборке для разных типов матраса этот размер и помечаем как выбраный
    params.width?.forEach((widthParam, widthIdx) => {
      params.height?.forEach((heightParam, heightIdx) => {
        if (!selectedSizes?.width || !selectedSizes?.height) return;
        if (
          widthParam.value === selectedSizes.width &&
          heightParam.value === selectedSizes.height
        ) {
          width.splice(widthIdx, 1, { ...widthParam, selected: true });
          height.splice(heightIdx, 1, { ...heightParam, selected: true });
          refOtherParams.current.width = [{ from: selectedSizes.width, to: selectedSizes.width }];
          refOtherParams.current.height = [
            { from: selectedSizes.height, to: selectedSizes.height },
          ];
        }
      });
    });

    return { ...params, width, height };
  }, [page.parameters, selectedVariants.peoples, selectedSizes]);

  const isCouple = useMemo(() => {
    return selectedVariants.peoples && selectedVariants.peoples[0].id === 'couple-adults';
  }, [selectedVariants]);

  const filteredPages = useMemo(() => {
    if (isCouple) return pages;

    return pages.filter((pageItem) => !pageItem.isPartner);
  }, [isCouple, pages]);

  const handleChangeStep = useCallback(
    (_e, step: StepData) => {
      selectStep(step);
    },
    [selectStep],
  );

  const transformedSteps = useMemo(() => {
    return steps.map((step) => {
      const stepPages = filteredPages.filter((pageItem) => pageItem.step === step.id);

      // Этот шаг был пропущен?
      let skipped = false;
      stepPages.forEach((pageItem) => {
        const hasSelected = pageItem.variants.some((variant) => variant.selected);

        if (!hasSelected) {
          skipped = true;
        }
      });

      return { ...step, skipped };
    });
  }, [filteredPages, steps]);

  const selectedPage = filteredPages.find((pageItem) => pageItem.selected);
  const selectedPageIndex = filteredPages.findIndex((pageItem) => pageItem.id === selectedPage.id);
  const isFirstPage = selectedPageIndex === 0;
  const isLastPage = selectedPageIndex === filteredPages.length - 1;
  const personalListTitles = [t('results.1'), t('results.2')];

  const priceRange: number[] = useMemo(() => {
    if (!selectedPage.prices) return [];
    //обрабатываем старый формат данных
    if (Array.isArray(selectedPage.prices)) return selectedPage.prices;

    if (isCouple) {
      return selectedPage.prices['couple-adults'];
    }
    return selectedPage.prices['adults-kids'];
  }, [isCouple, selectedPage]);

  const toggleVariant = useCallback(
    (targetVariant) => {
      //Если выбрали размер не селектором, выбираем из параметров

      if (selectedPage.id === 'peoples' && targetVariant.parameters) {
        const params = page.parameters.find((parameter) => parameter.id === targetVariant.id);
        const notInHeightRange = params?.height.every(
          (height) => height.value !== selectedSizes?.height,
        );
        const notInWidthRange = params?.width.every(
          (width) => width.value !== selectedSizes?.width,
        );
        if (notInHeightRange || notInWidthRange) {
          refOtherParams.current.width = [targetVariant.parameters.width];
          refOtherParams.current.height = [targetVariant.parameters.height];
        }
      }

      setPages((prevPages) => {
        return prevPages.map((pageItem) => {
          if (pageItem !== selectedPage) return pageItem;

          return {
            ...pageItem,
            variants: pageItem.variants.map((variant) => ({
              ...variant,
              selected: variant.id === targetVariant.id,
            })),
            skipped: false,
          };
        });
      });
    },
    [page.parameters, selectedPage, selectedSizes?.height, selectedSizes?.width],
  );

  const goToNextStep = useCallback(() => {
    const newStep = steps[selectedStepIndex + 1];

    if (newStep) selectStep(newStep);
  }, [selectStep, selectedStepIndex, steps]);

  const nextPage = useMemo(() => {
    return filteredPages[selectedPageIndex + 1];
  }, [filteredPages, selectedPageIndex]);

  const goToNextPage = useCallback(() => {
    if (nextPage) selectPage(nextPage);
  }, [selectPage, nextPage]);

  // Переходим либо на следующий шаг, либо на след страницу
  const goToNext = useCallback(() => {
    const stepPages = filteredPages.filter((pageItem) => pageItem.step === selectedStep.id);
    const isLast = stepPages[stepPages.length - 1] === selectedPage;

    if (isLast) {
      goToNextStep();
    } else {
      goToNextPage();
    }
  }, [goToNextPage, goToNextStep, filteredPages, selectedPage, selectedStep]);

  const handleSelect = useCallback(
    (_e, variant) => {
      toggleVariant(variant);

      if (selectedStep.id !== 'peoples') {
        setTimeout(goToNext, 400);
        return;
      }

      // Скролим к нужному элементу на первом шаге
      if (isMobileM && refSizes.current) {
        const top = window.pageYOffset + refSizes.current?.getBoundingClientRect().top;

        setTimeout(() => window.scrollTo({ top, behavior: 'smooth' }), 400);
      }
    },
    [goToNext, isMobileM, selectedStep.id, toggleVariant],
  );

  const handleChangeSize = useCallback((_e, [item]) => {
    setSelectedSizes({ width: item.data.width, height: item.data.height });
    refOtherParams.current.width = [{ from: item.data.width, to: item.data.width }];
    refOtherParams.current.height = [{ from: item.data.height, to: item.data.height }];
  }, []);

  const handleCalculate = useCallback(async () => {
    analytics.dispatchEvent('quiz.mattresses.findMattress');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setLoading(true);

    const paramNames = {
      width: t('params.width'),
      height: t('params.height'),
      prices: t('params.price'),
      hardness: t('params.hardness'),
      type: t('params.type'),
      weight: t('params.weight'),
    };

    try {
      const parameters = { ...refOtherParams.current };
      const properties = { ...parameters };
      const parametersForApi: Params = { parameters };

      // Собираем параметры для отправки на сервер
      Object.entries(selectedVariants).forEach(([key, variants]) => {
        variants.forEach((variant) => {
          if (!variant.value) return;
          if (!parameters[key]) parameters[key] = [];

          parameters[key].push(variant.value);
        });
      });

      if (selectPrice) {
        parametersForApi.prices = selectPrice;
        properties.prices = [selectPrice];
      }

      // Собираем параметры выбранных опций
      Object.entries(selectedVariants).forEach(([key, variants]) => {
        variants.forEach((variant) => {
          if (!variant.value) return;
          if (!properties[key]) properties[key] = [];

          properties[key].push(variant.title);
        });
      });

      // Формируем текстовые значения выбранных опций
      const newProperties = [];
      Object.entries(properties).forEach(([key, param]) => {
        const [first] = param as any[];
        const paramName = paramNames[key] || '';
        let text = '';

        switch (typeof first) {
          case 'string':
          case 'number': {
            text = first.toString();
            break;
          }

          case 'object': {
            const modyfyRange = (range) => {
              if (key === 'prices') {
                return `${formatPrice(range)}{currency}`;
              }
              return range;
            };
            const fromText = first.from ? `от ${modyfyRange(first.from)}` : '';
            const toText = first.to ? `до ${modyfyRange(first.to)}` : '';

            text = first.from === first.to ? first.from : `${fromText} ${toText}`;
            break;
          }

          default:
        }

        newProperties.push(`${paramName}: ${text}`.toLowerCase());
      });

      const newLists = await ApiPromo.getMattressesProducts(parametersForApi);

      setSelectedProperties(newProperties);
      setLists(newLists);
    } catch (err) {
      logger.log(err);
    } finally {
      setLoading(false);
    }
  }, [t, selectedVariants, selectPrice, logger, analytics]);

  const handleRepeat = useCallback(() => {
    refOtherParams.current = refInitialParams.current;

    setLists(null);
    setSteps(page.steps);
    setPages(pages);
  }, [page.steps, pages]);

  const handleSkip = useCallback(() => {
    goToNext();
  }, [goToNext]);

  const handleChangePrice = useCallback((_e, range) => {
    if (typeof range === 'number') {
      setSelectPrice({ from: 0, to: range });
    } else {
      setSelectPrice(null);
    }
  }, []);

  const skipButton = useMemo(() => {
    if (isFirstPage)
      return (
        <Button data-testid='next' className={styles.button} onClick={handleSkip}>
          {t('next')}
        </Button>
      );

    if (isLastPage)
      return (
        <Button data-testid='choose-mattrasses' className={styles.button} onClick={handleCalculate}>
          {t('choose-mattress')}
        </Button>
      );

    return (
      <UILink
        data-testid='skip-step'
        className={styles.skipButton}
        view='primary'
        underlined
        onClick={handleSkip}
      >
        {t('skip')}
      </UILink>
    );
  }, [handleCalculate, handleSkip, isFirstPage, isLastPage, t]);

  useEffect(() => {
    const newPage = pages.find((pageItem) => pageItem.step === selectedStep.id);

    selectPage(newPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStep]);

  //при смене региона перезапрашиваем результаты квиза
  useEffect(() => {
    if (lists && initialRegionRef.current !== region.id && !loading) {
      initialRegionRef.current = region.id;
      handleCalculate();
    }
  }, [handleCalculate, lists, loading, region]);

  return (
    <LayoutMain>
      <Page>
        <PageTopBar breadcrumbs={page.breadcrumbs} />
        <div className={styles.page} data-testid='page-quiz-mattrasses'>
          <div className={styles.contentWrapper}>
            {!lists && (
              <>
                <StepsIcons
                  className={styles.stepsIcons}
                  steps={transformedSteps}
                  selectedStep={selectedStepIndex}
                  onChangeStep={handleChangeStep}
                />

                <div className={styles.pageWrapper}>
                  <div className={styles.question}>{selectedPage.question}</div>
                  <div>
                    {(() => {
                      switch (selectedPage.step) {
                        case 'peoples': {
                          return (
                            <StepPeoples
                              refSizes={refSizes}
                              variants={selectedPage.variants}
                              parameter={selectedParams}
                              onSelect={handleSelect}
                              onChangeSize={handleChangeSize}
                            />
                          );
                        }

                        case 'weight': {
                          return (
                            <StepWeight variants={selectedPage.variants} onSelect={handleSelect} />
                          );
                        }

                        case 'hardness': {
                          return (
                            <StepHardness
                              variants={selectedPage.variants}
                              onSelect={handleSelect}
                            />
                          );
                        }

                        case 'type': {
                          return (
                            <StepType variants={selectedPage.variants} onSelect={handleSelect} />
                          );
                        }

                        case 'price': {
                          return (
                            <StepPrice
                              variants={selectedPage.variants}
                              ranges={priceRange}
                              onChangeRange={handleChangePrice}
                            />
                          );
                        }

                        default:
                          return null;
                      }
                    })()}

                    <div className={styles.buttonWrapper}>{skipButton}</div>
                  </div>
                </div>
              </>
            )}
          </div>

          {lists && (
            <div className={styles.resultsWrapper}>
              {lists.personalList.length > 0 ? (
                <>
                  <Section
                    title={personalListTitles[lists.personalList.length - 1] || t('results.3')}
                  >
                    <div className={styles.propertiesWrapper}>
                      <Properties properties={selectedProperties} />
                      <div>
                        <Repeat data-testid='go-again' onClick={handleRepeat}>
                          {t('repeat')}
                        </Repeat>
                      </div>
                    </div>
                    <div className={styles.personalList}>
                      {lists.personalList.map((product, index) => (
                        <CardInView
                          key={product.id}
                          className={styles.personalProduct}
                          id={product.id}
                          listTitle='Подобрать матрас идеальные'
                          position={index}
                          card={product}
                          cardType='product'
                        >
                          <ProductCard product={product} />
                        </CardInView>
                      ))}
                    </div>
                  </Section>
                </>
              ) : (
                <NotFound title={t('not-found')} onRepeat={handleRepeat} />
              )}

              {lists.categoryList.length > 0 && (
                <Section className={styles.subSection} title={t('related.1')}>
                  <ProductCatalog className={styles.catalog} products={lists.categoryList} />
                </Section>
              )}
            </div>
          )}

          {loading && <Spinner />}
        </div>
      </Page>
    </LayoutMain>
  );
};

export default PageQuizMattrasses;
